import React, { useCallback, useRef } from 'react';

export interface UseFilePickerOptions {
  /** Фильтр системного окна выбора, как атрибут `accept`: `'image/*'`, `'.json,application/json'`. */
  accept?: string;
  /** Выбранный файл. Отмена окна сюда не приходит. */
  onPick: (file: File) => void;
  /** `data-testid` скрытого input — тесты кладут в него файл через `fireEvent.change`. */
  testId?: string;
}

export interface FilePicker {
  /** Открыть системное окно выбора файла. Вызывать из обработчика клика — браузер требует жест пользователя. */
  open: () => void;
  /** Скрытый `<input type="file">` — отрендерить один раз где угодно рядом с кнопкой. */
  input: React.ReactElement;
}

/**
 * Выбор файла своей кнопкой: скрытый `<input type="file">` и `open()`, который его кликает. Кнопку рисует
 * вызывающий (`UDButton`, `UDIconButton`), у неё и подпись для скринридера.
 *
 * После выбора value инпута сбрасывается, поэтому тот же файл можно выбрать второй раз подряд — иначе браузер
 * не пришлёт `change`.
 */
export function useFilePicker({ accept, onPick, testId }: UseFilePickerOptions): FilePicker {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  const open = useCallback(() => inputRef.current?.click(), []);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) onPickRef.current(file);
  }, []);

  const input = <input ref={inputRef} type="file" accept={accept} hidden data-testid={testId} onChange={handleChange} />;

  return { open, input };
}

export default useFilePicker;
